import { useState } from 'react';
import { X } from 'lucide-react';

interface CreateBoardDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string) => Promise<void>;
}

export function CreateBoardDialog({ open, onClose, onCreate }: CreateBoardDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Board name is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onCreate(name.trim(), description.trim());
      setName('');
      setDescription('');
      onClose();
    } catch {
      setError('Failed to create board. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-board-title"
      >
        <div className="flex items-center justify-between mb-5">
          <h2
            id="create-board-title"
            className="text-base font-semibold"
            style={{ color: '#2F241B', fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            New Board
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-[#F5F1EC] transition-colors"
            style={{ color: '#A39686' }}
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B5D52' }}>
              Name <span aria-hidden="true">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              placeholder="e.g. Sprint Board, Backlog"
              autoFocus
              className="w-full px-3 py-2 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-[#D97F6F]/40"
              style={{ borderColor: '#E8E0D5', color: '#2F241B' }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6B5D52' }}>
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              className="w-full px-3 py-2 text-sm rounded-md border focus:outline-none focus:ring-2 focus:ring-[#D97F6F]/40"
              style={{ borderColor: '#E8E0D5', color: '#2F241B' }}
            />
          </div>

          {error && (
            <p className="text-xs" style={{ color: '#E5534B' }}>{error}</p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium transition-colors hover:bg-[#F5F1EC]"
              style={{ color: '#6B5D52' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim()}
              className="px-4 py-2 rounded-md text-white text-sm font-semibold transition-all hover:brightness-110 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#D97F6F' }}
            >
              {saving ? 'Creating…' : 'Create Board'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
